import { useState } from "react";
import { Flag } from "lucide-react";
import { reportProblem } from "@/lib/api";

// Tiny per-question "report a problem" affordance. Collapsed to a text button;
// expands to an optional-reason box. Excluded from print/PDF via no-print +
// data-no-email (the PDF clone strips both).
export default function ReportProblem({ question, shownAnswer, worksheetId, problemId, topic, ccssCode }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent

  const send = async () => {
    if (state !== "idle") return;
    setState("sending");
    try {
      await reportProblem({
        question: String(question || "").slice(0, 2000),
        shown_answer: shownAnswer != null ? String(shownAnswer) : null,
        reason: reason.trim() || null,
        worksheet_id: worksheetId || null,
        problem_id: problemId || null,
        topic: topic || null,
        ccss_code: ccssCode || null,
        page: typeof window !== "undefined" ? window.location.pathname : null,
      });
      setState("sent");
    } catch {
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <p className="no-print mt-2 text-xs font-bold text-green-700" data-no-email data-testid="report-sent">
        ✓ Thanks — we'll review this problem.
      </p>
    );
  }

  return (
    <div className="no-print mt-2" data-no-email>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0A0A0A]/45 underline underline-offset-2 hover:text-brand-coral"
        data-testid="report-toggle"
      >
        <Flag strokeWidth={3} className="w-3 h-3" />
        Report a problem
      </button>
      {open && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start" data-testid="report-form">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="What's wrong? (optional — e.g. wrong answer, confusing wording)"
            className="w-full max-w-sm text-xs border-2 border-[#0A0A0A]/30 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#0A0A0A] bg-white"
          />
          <button
            type="button"
            onClick={send}
            disabled={state === "sending"}
            className="brutal-btn bg-brand-coral text-white px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="report-submit"
          >
            {state === "sending" ? "Sending…" : "Send report"}
          </button>
        </div>
      )}
    </div>
  );
}
